import React, { useEffect, useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
	useAnalyticsEvents,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import ImportError from '../../common/assets/ImportErrorIllustration.svg';
import type { SupportedThirdParty } from '../../common/constants/import-type';
import { X2C_SCREENS } from '../../common/constants/metrics';
import type { StatisticsType } from '../../common/constants/steps';
import { PageHeading, ErrorHeading } from '../../common/ui';
import { CloseAndReturnButton } from '../../common/ui/close-and-return-button';
import { StepFooterStandard } from '../../common/ui/step-footer';
import { ProgressBarStandard, StepProgressBar } from '../../common/ui/step-progress-bar';
import { useContentForThirdParty } from '../../common/utils';
import { useProjectData, useThirdPartyState } from '../../controllers/import-state';
import { useAccessTokenModalState } from '../../controllers/ui-state';
import { useProductSteps } from '../../controllers/utils';
import { useThirdPartyWorkspaces } from '../../services/third-party-workspaces';
import { EntityContent } from './entity-content';
import { EntityStats } from './entity-stats';
import messages from './messages';
import { ThirdPartyIllustration } from './third-party-illustration';

export type SelectThirdPartyEntityScreenProps = {
	onReturn: () => void;
	onNext: () => void;
	thirdParty: SupportedThirdParty;
	stats: StatisticsType[];
	onCloseAndReturnToUploadStep: () => void;
	hasSearchableEntities?: boolean;
	shouldShowPlaceholderIcons?: boolean;
	shouldShowFolderSelector?: boolean;
	hideWorkspaceSelector?: boolean;
	shouldShowSpaceSelector?: boolean;
	shouldShowAccountInfoIfUsingPAT?: boolean;
	isMultipleEntitiesSupported?: boolean;
};

export const SelectThirdPartyEntityScreen = ({
	onReturn,
	onNext,
	thirdParty,
	stats,
	onCloseAndReturnToUploadStep,
	hasSearchableEntities,
	shouldShowPlaceholderIcons,
	shouldShowFolderSelector,
	hideWorkspaceSelector,
	shouldShowSpaceSelector,
	shouldShowAccountInfoIfUsingPAT,
	isMultipleEntitiesSupported = false,
}: SelectThirdPartyEntityScreenProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { heading, noAccountErrorHeader } = useContentForThirdParty(thirdParty);
	const { productSteps, currentStepIndex } = useProductSteps();
	const [{ type }] = useProjectData();
	const [{ selectedEntityId, selectedWorkspaceId, selectedEntities }, { setThirdPartyState }] =
		useThirdPartyState();

	const [, { setRequestAuthenticationToken: requestAuthenticationToken }] =
		useAccessTokenModalState();
	const { hasAccountErrorOccurred } = useThirdPartyWorkspaces({
		thirdParty,
		requestAuthenticationToken,
	});

	useEffect(() => {
		if (hasAccountErrorOccurred) {
			fireUIAnalytics(createAnalyticsEvent({}), 'noThirdPartyAccount found', {
				thirdParty,
			});
		}
	}, [hasAccountErrorOccurred, thirdParty, createAnalyticsEvent]);

	const handleOnReturn = useCallback(() => {
		setThirdPartyState({
			selectedWorkspaceId: null,
			selectedSpaceId: null,
			selectedSpaceName: null,
			selectedEntities: null,
			selectedEntityId: null,
			selectedEntityName: null,
			selectedEntityStyle: null,
			entities: null,
			workspaces: null,
			entityStats: null,
			members: null,
			spaces: null,
		});

		onReturn();
	}, [onReturn, setThirdPartyState]);

	const progressBarComponent = useMemo(() => {
		if (fg('enable-x2c-standardised-flow')) {
			return (
				<>
					<ProgressBarStandard
						productSteps={productSteps}
						currentStepIndex={currentStepIndex}
						onClose={onCloseAndReturnToUploadStep}
						shouldShowCancelModal={
							!hasAccountErrorOccurred && (selectedWorkspaceId != null || selectedEntityId != null)
						}
					/>
					<Box xcss={paddingForProgressBar} />
				</>
			);
		}
		return (
			<>
				<CloseAndReturnButton
					onCloseAndReturnToUploadStep={onCloseAndReturnToUploadStep}
					shouldShowCancelModal={
						!hasAccountErrorOccurred && (selectedWorkspaceId != null || selectedEntityId != null)
					}
				/>
				<StepProgressBar productSteps={productSteps} currentStepIndex={currentStepIndex} />
			</>
		);
	}, [
		hasAccountErrorOccurred,
		onCloseAndReturnToUploadStep,
		selectedWorkspaceId,
		selectedEntityId,
		productSteps,
		currentStepIndex,
	]);

	const renderStepFooterComponent = useMemo(() => {
		if (fg('enable-x2c-standardised-flow')) {
			return <StepFooterStandard alignRight onReturn={handleOnReturn} shouldDisableNext />;
		}
		return (
			<>
				<Button
					onClick={handleOnReturn}
					testId="project-create-importer-upload-flow.ui.select-third-party-entity-screen.back-button"
				>
					{formatMessage(messages.back)}
				</Button>
			</>
		);
	}, [handleOnReturn, formatMessage]);

	if (hasAccountErrorOccurred) {
		return (
			<Wrapper>
				{progressBarComponent}
				<AccountErrorWrapper>
					<Illustration src={ImportError} aria-describedby="import-error" role="img" alt="" />
					<ErrorHeading>{noAccountErrorHeader}</ErrorHeading>
					{renderStepFooterComponent}
				</AccountErrorWrapper>
			</Wrapper>
		);
	}

	return (
		<ContextualAnalyticsData
			sourceName={X2C_SCREENS.SELECT_THIRD_PARTY_ENTITY_SCREEN}
			sourceType={SCREEN}
			attributes={{ thirdParty }}
		>
			<Wrapper data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.wrapper">
				{progressBarComponent}
				<SectionWrapper>
					<ContentWrapper>
						<PageHeading>{heading}</PageHeading>
						<EntityContent
							thirdParty={thirdParty}
							shouldShowPlaceholderIcons={!!shouldShowPlaceholderIcons}
							shouldShowFolderSelector={!!shouldShowFolderSelector}
							hideWorkspaceSelector={!!hideWorkspaceSelector}
							shouldShowAccountInfoIfUsingPAT={!!shouldShowAccountInfoIfUsingPAT}
							shouldShowSpaceSelector={!!shouldShowSpaceSelector}
							isMultipleEntitiesSupported={isMultipleEntitiesSupported}
							onReturn={onReturn}
							onNext={onNext}
							hasSearchableEntities={!!hasSearchableEntities}
						/>
					</ContentWrapper>
					<ContentWrapper>
						<ThirdPartyIllustration thirdParty={thirdParty} projectType={type} />
						{(selectedEntityId || (isMultipleEntitiesSupported && selectedEntities)) && (
							<EntityStats
								thirdParty={thirdParty}
								stats={stats}
								removeBackground
								requestAuthenticationToken={requestAuthenticationToken}
								isMultipleEntitiesSupported={isMultipleEntitiesSupported}
							/>
						)}
					</ContentWrapper>
				</SectionWrapper>
				<FireScreenAnalytics />
			</Wrapper>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexFlow: 'column',
	width: '100%',
	boxSizing: 'border-box',
	padding: `${token('space.300', '24px')} ${token('space.1000', '80px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.input', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		width: 'fit-content',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexFlow: 'column',
	width: '448px',
	margin: `${token('space.1000', '80px')} ${token('space.800', '64px')}`,
	boxSizing: 'border-box',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionWrapper = styled.div({
	display: 'flex',
	flexFlow: 'row',
	justifyContent: 'center',
	flexWrap: 'wrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AccountErrorWrapper = styled.div({
	display: 'flex',
	flexFlow: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Illustration = styled.img({
	maxWidth: '408px',
	maxHeight: '288px',
	width: '50%',
	height: '50%',
});

const paddingForProgressBar = xcss({
	paddingTop: 'space.800',
});
